
import { supabase } from "@/integrations/supabase/client";
import { offlineDb } from "@/db/offlineDb";
import { UserCategory } from "@/types";
import OfflineLogger from "@/utils/offlineLogger";
import { getCachedUserId, setCachedUser } from "@/services/auth/sessionCache";

export class CategoryDataService {
  private currentUserId: string | null = null;
  private userIdPromise: Promise<string | null> | null = null;

  async getCurrentUserId(): Promise<string | null> {
    if (this.userIdPromise) {
      return this.userIdPromise;
    }

    this.userIdPromise = this.fetchCurrentUserId();
    const userId = await this.userIdPromise;
    this.userIdPromise = null;
    
    if (userId !== this.currentUserId) {
      this.currentUserId = userId;
    }
    
    return userId;
  }

  private async fetchCurrentUserId(): Promise<string | null> {
    try {
      const cached = await getCachedUserId();
      if (cached) {
        return cached;
      }

      const { data: userData, error } = await supabase.auth.getUser();
      if (!error && userData.user) {
        setCachedUser(userData.user);
        return userData.user.id;
      }

      return null;
    } catch (error) {
      OfflineLogger.error('CategoryDataService.fetchCurrentUserId', error);
      return null;
    }
  }

  convertToUserCategory(syncable: any): UserCategory {
    return {
      id: syncable.id,
      name: syncable.name || '',
      icon: syncable.icon,
      color: syncable.color,
      textColor: syncable.text_color,
      position: syncable.position,
      visibile: syncable.visibile,
      userId: syncable.user_id,
      createdAt: new Date(syncable.created_at),
      updatedAt: new Date(syncable.updated_at)
    };
  }

  async loadLocalCategories(): Promise<UserCategory[]> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return [];
      }


      const categories = await offlineDb.categories
        .where('user_id')
        .equals(userId)
        .and(category => category._syncStatus !== 'deleted')
        .toArray();

      const sortedCategories = categories.sort((a, b) => (a.position || 0) - (b.position || 0));
      const userCategories = sortedCategories.map(cat => this.convertToUserCategory(cat));
      
      return userCategories;
    } catch (error) {
      console.error('[CategoryDataService.loadLocalCategories] Error:', error);
      OfflineLogger.error('CategoryDataService.loadLocalCategories', error);
      return [];
    }
  }

  async fetchServerCategories(): Promise<UserCategory[]> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return [];
      }


      const { data: serverCategories, error } = await supabase
        .from('user_categories')
        .select('*')
        .eq('user_id', userId)
        .order('position', { ascending: true });

      if (error) {
        console.error('[CategoryDataService.fetchServerCategories] Supabase error:', error);
        OfflineLogger.error('CategoryDataService.fetchServerCategories', error);
        return [];
      }


      return serverCategories?.map(cat => this.convertToUserCategory(cat)) || [];
    } catch (error) {
      console.error('[CategoryDataService.fetchServerCategories] Error:', error);
      OfflineLogger.error('CategoryDataService.fetchServerCategories', error);
      return [];
    }
  }

  async updateLocalCategories(categories: any[]): Promise<void> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) return;

      const categoriesWithSync = categories.map(cat => ({
        ...cat,
        _syncStatus: 'synced' as const,
        _lastModified: Date.now()
      }));

      
      // ✅ FIX: Usa bulkPut invece di bulkAdd per evitare errori di chiavi duplicate
      // Prima elimina le categorie esistenti per evitare conflitti
      await offlineDb.categories.where('user_id').equals(userId).delete();
      
      // Poi inserisci le nuove categorie
      await offlineDb.categories.bulkPut(categoriesWithSync);
      
      OfflineLogger.info('CategoryDataService.updateLocalCategories', `Updated ${categories.length} categories locally`);
    } catch (error) {
      console.error('[CategoryDataService.updateLocalCategories] Error:', error);
      OfflineLogger.error('CategoryDataService.updateLocalCategories', error);
      throw error;
    }
  }
}

export const categoryDataService = new CategoryDataService();
